'use memo';

import { useId, useState } from 'react';
import IconButton from '@/components/Button/IconButton';
import { BusinessPartnerCombobox } from '@/components/Combobox/BusinessPartnerCombobox';
import type { BusinessPartnerOption } from '@/components/Combobox/BusinessPartnerCombobox/-query/type';
import Icon from '@/components/Icon/Icon';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { cn } from '@/lib/cn';
export type BusinessPartner = {
  id: string;
  name: string;
};
type Props = {
  value?: BusinessPartner;
  onChange: (option?: BusinessPartner) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
};
export function BusinessPartnerSelect(props: Props) {
  const tc = useCommonTranslation();
  const [open, setOpen] = useState(false);
  const popoverId = useId();
  const canClear = Boolean(props.allowEmpty && !props.disabled && props.value);
  const valueToLabelCache = useValueToLabelCache({
    base: props.value ? {
      [props.value.id]: props.value.name
    } : undefined
  });
  const handleValueChange = (value: string | null, option?: BusinessPartnerOption) => {
    if (value && option) {
      props.onChange({
        id: option.value,
        name: option.label
      });
    } else {
      props.onChange(undefined);
    }
    setOpen(false);
  };
  return <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <div className="relative inline-block" style={props.width ? {
      width: props.width
    } : undefined}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverId} className={cn('relative w-full justify-start overflow-hidden text-ellipsis pr-10', !props.value && 'text-neutral-muted-on-fill')} disabled={props.disabled}>
            <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
              {props.value?.name || props.placeHolder || tc('pleaseSelect')}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label="クリア" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          props.onChange(undefined);
        }} onPointerDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }} />
          </div>}
      </div>
      <PopoverContent id={popoverId} className="w-full overflow-hidden p-0" style={props.width ? {
      width: props.width
    } : undefined} align="start">
        <BusinessPartnerCombobox value={props.value?.id ?? null} onValueChange={handleValueChange} valueToLabelCache={valueToLabelCache} />
      </PopoverContent>
    </Popover>;
}